import { useMemo } from 'react'
import { useDispatch, useSelector } from '@tabeeb/store'
import { Formik, Form, Field } from 'formik'
import { string, array, object } from 'yup'

import { DialogContent, DialogTitle } from '@mui/material'
import { maxLength, noWhitespaces, requiredField } from '@tabeeb/modules/shared/utils/validationErrorMessages'
import {
  AIObjectPropertyResponse,
  AIObjectPropertyType,
  AutofillSource,
  UniqueAIObjectResponse,
} from '@tabeeb/contracts/microservices/swagger-ai-service'
import { ResizableDialog } from '@collaborate-health/uikit'
import CircularProgressBar from '@tabeeb/modules/shared/tabeebCircularProgressBar'
import * as aiActions from '../../actions'
import { getSelectedUniqueAIObject } from '../../selectors'

import AutofillButton from './AutofillButton'
import TextFieldComponent from '../TextFieldComponent'
import UniqueAIObjectPropertiesField from '../UniqueAIObjectPropertiesField'
import { validAIObjectPropertySchema } from '../../services'
import DialogActionsWithFormik from '../DialogActionsWithFormik'
import { useAIObject } from '../../hooks'
import { Header } from './EditUniqueAIObjectDialog.styles'

const validationSchemaSchema = object().shape({
  name: string().strict().trim(noWhitespaces).max(500, maxLength(500)).required(requiredField),
  properties: array().of(
    object()
      .shape({
        value: validAIObjectPropertySchema,
      })
      .required()
  ),
})

const EditUniqueAIObjectDialog = () => {
  const dispatch = useDispatch()
  const selectedUniqueAIObject: UniqueAIObjectResponse = useSelector(getSelectedUniqueAIObject)
  const { aiObject = { Name: '', Properties: [] as AIObjectPropertyResponse[] }, loading } = useAIObject({
    id: selectedUniqueAIObject?.AIObjectId,
  })

  interface UniqueAIObjectPropertyFormValue {
    id: number | undefined
    aiObjectPropertyId: number
    name: string
    type: AIObjectPropertyType
    isDisabled: boolean
    isEditable: boolean
    isHidden: boolean
    value: any
    ordinal: number
    autofillSource: AutofillSource
    selectionListId: number | null | undefined
  }

  interface EditUniqueAIObjectFormValues {
    name: string
    properties: UniqueAIObjectPropertyFormValue[]
  }

  const handleSubmit = (values: EditUniqueAIObjectFormValues) => {
    const model = {
      Id: selectedUniqueAIObject.Id,
      Name: values.name,
      Properties: values.properties
        .filter(
          (property) =>
            (property.type !== AIObjectPropertyType.Dropdown &&
              property.value !== null &&
              property.value !== undefined &&
              property.value !== '') ||
            (property.type === AIObjectPropertyType.Dropdown && property?.value?.Value)
        )
        .map((property) => ({
          Id: property.id,
          Type: property.type,
          AIObjectPropertyId: property.aiObjectPropertyId,
          Value: (() => {
            if (property.type === AIObjectPropertyType.Dropdown) {
              return [
                {
                  Value: property.value.Value,
                  SLIId: property.value.Id,
                },
              ]
            }
            return property.value
          })(),
          Ordinal: property.ordinal,
        })),
    }

    dispatch(aiActions.updateUniqueAiObjectRequest(model))
    dispatch(aiActions.setUniqueAiObjectForEdit(null))
    dispatch(aiActions.setUniqueAiObjectAnnotationForEdit(null))
  }

  const handleClose = (reason: string, handleReset: () => void) => {
    if (reason && reason === 'backdropClick') {
      return
    }

    dispatch(aiActions.setUniqueAiObjectForEdit(null))
    dispatch(aiActions.setUniqueAiObjectAnnotationForEdit(null))
    handleReset()
  }

  const selectedUniqueAIObjectPropertiesMap = useMemo(() => {
    if (!selectedUniqueAIObject) {
      return {}
    }

    return Object.fromEntries(
      selectedUniqueAIObject.Properties.map((property) => [property.AIObjectPropertyId, property])
    )
  }, [selectedUniqueAIObject])

  const initialValues: EditUniqueAIObjectFormValues = useMemo(
    () => ({
      name: selectedUniqueAIObject?.Name || '',
      properties: aiObject?.Properties
        ? aiObject.Properties.map((aiObjectProperty) => ({
            id: selectedUniqueAIObjectPropertiesMap[aiObjectProperty.Id]?.Id || undefined,
            aiObjectPropertyId: aiObjectProperty.Id,
            name: aiObjectProperty.Name,
            type: aiObjectProperty.Type,
            isDisabled: aiObjectProperty.IsDisabled,
            isEditable: aiObjectProperty.IsEditable,
            isHidden: aiObjectProperty.IsHidden,
            value:
              selectedUniqueAIObjectPropertiesMap[aiObjectProperty.Id]?.Value === null ||
              aiObjectProperty.Type !== AIObjectPropertyType.Dropdown
                ? selectedUniqueAIObjectPropertiesMap[aiObjectProperty.Id]?.Value
                : {
                    Id: selectedUniqueAIObjectPropertiesMap[aiObjectProperty.Id]?.Value[0].SLIId,
                    Value: selectedUniqueAIObjectPropertiesMap[aiObjectProperty.Id]?.Value[0].Value,
                  },
            ordinal: aiObjectProperty.Ordinal,
            autofillSource: aiObjectProperty.AutofillSource,
            selectionListId: aiObjectProperty.SelectionListId,
          }))
        : [],
    }),
    [aiObject.Properties, selectedUniqueAIObject?.Name, selectedUniqueAIObjectPropertiesMap]
  )

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchemaSchema}
    >
      {({ values, errors, handleReset }) => (
        <ResizableDialog
          allowBackgroundInteraction
          open={Boolean(selectedUniqueAIObject)}
          onClose={(event, reason) => handleClose(reason, handleReset)}
          header={
            <Header>
              <DialogTitle sx={{ pb: 0 }}>Edit properties</DialogTitle>
              <AutofillButton uniqueAIObject={selectedUniqueAIObject} />
            </Header>
          }
          content={
            <>
              <DialogContent>
                {loading ? (
                  <CircularProgressBar />
                ) : (
                  <Form>
                    <Field
                      disabled
                      name='name'
                      placeholder='Name'
                      label='Name'
                      variant='outlined'
                      sx={{ marginBottom: 1 }}
                      component={TextFieldComponent}
                    />
                    <Field name='properties' component={UniqueAIObjectPropertiesField} />
                  </Form>
                )}
              </DialogContent>
              <DialogActionsWithFormik />
            </>
          }
        />
      )}
    </Formik>
  )
}

export default EditUniqueAIObjectDialog

